/**
 * Shared mobile top-level navigation model — one source of truth for the
 * header icon strip (MobileHeader) and the content swipe-pager (MobileShell)
 * so their page order and active-detection can't drift.
 */
import { useMemo, type ComponentType } from 'react'
import { HardDrive, LayoutGrid, BookKey, MessageSquare } from 'lucide-react'
import { BrandLogo, useBrand } from '@papan/brand/react'
import type { BrandLanding } from '@papan/brand/client'
import { chatHomeAvailableFor, useKeybookEnabled, useLandingExperience } from '../lib/landing'
import { IDENTITY_ONLY_HIDDEN_VIEWS, PDAppIcon, type View } from '@papan/core-ui/ui'

export type MobileTab = 'launcher' | 'chat' | 'drive' | 'workspace' | 'apps' | 'keybook'

export interface MobileTabDef {
  key: MobileTab
  label: string
  /** Any icon accepting `size` / `className` — Lucide icons and the brand
   *  mark (below) both satisfy this. */
  icon: ComponentType<{ size?: number; className?: string }>
  /** Route segment after the tenant prefix (drive's root list is /folder). */
  path: string
}

// The brand mark the app chrome already uses (same glyph as MobileHeader / the
// desktop taskbar), size-adapted for the icon slot — so the chat tab matches
// the logo shown top-left. Was a hand-inlined prism hardcoded to `#AD46FF`;
// `<BrandLogo />` now owns the glyph and its colour. Decorative because the
// tab renders its own visible label right next to it.
const BrandMark = ({ size = 24, className }: { size?: number; className?: string }) => (
  <BrandLogo decorative size={size} className={className} />
)

/**
 * Left→right order — also the swipe order (swipe left = next).
 *
 * The HOME tab is always first and always carries the brand mark, mirroring
 * desktop, where the taskbar logo opens whatever `experience.landing` names
 * (App.tsx `homeView`). Which tab that is therefore depends on the brand:
 *
 *   landing: 'chat'      → [chat*, drive, workspace, apps, keybook]
 *   landing: 'launcher'  → [launcher*, drive, workspace, apps, keybook]
 *
 * A launcher DEPLOYMENT has no chat tab: chat is off there (#368, owner
 * 2026-10-29 — `chatHomeAvailableFor` in lib/landing.ts is the rule). The
 * workspace's AI panel is still reachable through the Project tab.
 *
 * `chatOn` is separate from `landing` because the HOME can be the launcher on
 * a chat deployment too: an identity-only tenant is sent there (`useMobileTabs`
 * below), and its chat tab stays, demoted, as it always was.
 *
 * A chat-landing deployment gets no launcher tab at all: its grid is usually
 * uncurated, and a permanently empty tab is worse than no tab. `/…/launcher`
 * still routes (MobileShell) — it just isn't advertised.
 *
 * An empty `label` is the "substitute the brand product name" marker, resolved
 * by `useMobileTabs()` — a pure builder cannot read the brand.
 */
function tabsFor(landing: BrandLanding, chatOn: boolean = landing === 'chat'): MobileTabDef[] {
  const rest: MobileTabDef[] = [
    // Drive lands on the HOME surface (greeting/search/projects/recents —
    // the same DriveHomeView as desktop); folder browsing is /folder.
    { key: 'drive', label: 'Drive', icon: HardDrive, path: 'home' },
    { key: 'workspace', label: 'Project', icon: LayoutGrid, path: 'workspace' },
    // #1484: the PDApps shell (`/apps`) — the desktop taskbar's PDApps window.
    { key: 'apps', label: 'PDApps', icon: PDAppIcon, path: 'apps' },
    { key: 'keybook', label: 'Keybook', icon: BookKey, path: 'keybook' },
  ]
  return landing === 'launcher'
    ? [
        // "Apps", not the brand name: the header cutout already renders the
        // product/tenant name on the line below the eyebrow, and the home tab
        // taking the empty-label marker would print it twice.
        { key: 'launcher', label: 'Apps', icon: BrandMark, path: 'launcher' },
        // Demoted from home, so it gives up the brand mark and takes a literal
        // label — the other non-home tabs are fixed product nouns too. Only
        // where chat is on at all (see the docblock).
        ...(chatOn ? [{ key: 'chat' as const, label: 'Chat', icon: MessageSquare, path: 'chat' }] : []),
        ...rest,
      ]
    : [{ key: 'chat', label: '', icon: BrandMark, path: 'chat' }, ...rest]
}

/** Exported for tests: the table for a landing OTHER than the live one. */
export { tabsFor as mobileTabsFor }

/**
 * THE tab table — this deployment's landing, with brand-dependent labels
 * resolved. The single source for both the header strip and the nav overlay.
 *
 * ── Why there is no `MOBILE_TABS` const any more ──
 *
 * There was one: `export const MOBILE_TABS = tabsFor(landingView())`, evaluated
 * at import. That was correct only while the landing was baked into the
 * document at build time. Now that an operator can flip it from the console,
 * a module-level const is not merely stale — it is FROZEN for the life of the
 * document, and no amount of making the value eventually correct reaches it.
 * On the surface where the landing matters most (a phone, whose only top-level
 * nav is this strip), the tab table would have kept naming the build default
 * forever. It is derived per render from the store instead.
 *
 * At most one tab is brand-derived, and only on a chat landing (the chat tab is
 * then the product-name "home"). The rest are fixed product nouns. An empty
 * `label` is the marker, so adding another brand-labelled tab needs no change
 * here.
 */
/**
 * @param identityOnly The active tenant is identity-only (0175, §8).
 *
 * A PARAMETER rather than a `useIdentityOnly()` call inside, deliberately. This
 * hook's inputs are the brand and the landing — both ambient, neither
 * tenant-scoped — and reading the tenant here would couple the nav MODEL to the
 * tenant context. That is not hypothetical tidiness: it broke four cases in
 * `nav.test.tsx`, which renders the hook to assert the strip the header actually
 * paints and now had to construct a `TenantProvider` to ask a question about
 * labels. Both callers already sit inside the shell and have the flag.
 */
export function useMobileTabs(identityOnly = false): MobileTabDef[] {
  const { productName } = useBrand()
  const landing = useLandingExperience()
  // §8: an identity-only tenant has no Drive, Workspace or Keybook, so their
  // tiles go — this carousel is the phone's ONLY top-level nav, so leaving them
  // would be three tiles that each open the "no drive here" notice.
  //
  // The landing is overridden too, for the same reason desktop does it: the
  // chat home resolves a workspace this tenant cannot have, so `launcher` is
  // home whatever the deployment configured. Passing it through `tabsFor` also
  // keeps the launcher TILE present, which a chat-configured deployment would
  // otherwise not advertise at all — leaving an identity-only user with a home
  // tab and nothing else.
  const effectiveLanding: BrandLanding = identityOnly ? 'launcher' : landing
  // The DEPLOYMENT's answer, not the effective home's: an identity-only tenant
  // on a chat deployment keeps its chat tab (#368).
  const chatOn = chatHomeAvailableFor(landing)
  // Ambient like the brand and the landing — the keybook switch (0179) is
  // platform-wide, not tenant-scoped, so reading it here keeps the docblock's
  // rule intact (only `identityOnly` is the tenant's business, and it arrives
  // as the parameter).
  const keybookEnabled = useKeybookEnabled()
  return useMemo(
    () => tabsFor(effectiveLanding, chatOn)
      .filter((tab) => !(identityOnly && IDENTITY_ONLY_HIDDEN_VIEWS.includes(tab.key as View)))
      .filter((tab) => keybookEnabled || tab.key !== 'keybook')
      .map((tab) => (tab.label ? tab : { ...tab, label: productName })),
    [effectiveLanding, chatOn, identityOnly, keybookEnabled, productName],
  )
}

/** Which top-level page a pathname belongs to. Files open from the drive, so
 *  /file/* keeps Drive active.
 *
 *  This answers "which page am I on", NOT "which tab is on the strip" — it can
 *  return `launcher` on a chat landing, whose table has no such entry. Both
 *  consumers already handle a miss (`find(...) ?? tabs[0]` in MobileHeader;
 *  `Math.max(0, findIndex(...))` in MobileNavOverlay), which is what makes the
 *  honest answer safe. Returning 'drive' instead would light up the Drive tile
 *  while the launcher is on screen — the same bug the /app/ fallback already
 *  forced both callers to special-case.
 *
 *  Deliberately PURE, and deliberately NOT given the tab list as a parameter:
 *  it derives the page from the pathname alone and never consults the table, so
 *  the table becoming live changed nothing here. Threading the list through
 *  would couple a pathname classifier to a React store for no behaviour. */
export function activeTab(pathname: string): MobileTab {
  if (/\/launcher(\/|$)/.test(pathname)) return 'launcher'
  if (/\/chat(\/|$)/.test(pathname)) return 'chat'
  if (/\/workspace(\/|$)/.test(pathname)) return 'workspace'
  if (/\/keybook(\/|$)/.test(pathname)) return 'keybook'
  // `/apps` only — a running app is `/app/<id>` (no `s`), a drive page.
  if (/\/apps(\/|$)/.test(pathname)) return 'apps'
  return 'drive'
}
